/**
 * AI Chat Service - Enhanced natural language to code conversion
 * 
 * This service integrates with the AI Code Generator backend and Timeline Code Templates
 * to provide intelligent code generation for timeline manipulation.
 */

import timelineCodeTemplates from './codeTemplates.js';
import timelineQuerySystem from './timelineQuerySystem.js';
import timelineStateManager from './timelineStateManager.js';
import smartCodeGenerator from './smartCodeGenerator.js';
import { chatAPI } from './api.js';

class AIChatService {
  constructor() {
    this.conversationHistory = [];
    this.lastGeneratedCode = null;
    this.codeExecutionHistory = [];
    this.debugMode = false;
  }

  /**
   * Process user message and generate timeline code
   */
  async processMessage(message, timelineContext, projectId, sessionId, options = {}) {
    try {
      // Get enhanced context from state manager if available
      const stateManagerContext = timelineStateManager.getEnhancedContext?.() || {};
      
      // Merge timeline context with state manager context
      const enhancedTimelineContext = {
        ...timelineContext,
        // Add conversation history for better context awareness
        recentConversation: stateManagerContext.recentConversation || [],
        editingPatterns: stateManagerContext.editingPatterns || {},
        userPreferences: stateManagerContext.userPreferences || {},
        sessionInfo: stateManagerContext.sessionInfo || {}
      };

      // Step 1: Check if this is a query/search request
      const queryResult = this._tryTimelineQuery(message, enhancedTimelineContext);
      if (queryResult) {
        return this._formatQueryResponse(queryResult, message);
      }

      // Step 2: Try template matching with enhanced context
      const templateMatch = this._tryTemplateMatching(message, enhancedTimelineContext, options);
      if (templateMatch) {
        return this._formatTemplateResponse(templateMatch, message);
      }

      // Step 3: Use AI for complex requests with full context
      const aiResponse = await this._generateWithAI(message, enhancedTimelineContext, projectId, sessionId, options);
      
      // Step 4: Enhance with execution metadata
      return this._enhanceAIResponse(aiResponse, message, enhancedTimelineContext);

    } catch (error) {
      console.error('AI Chat processing failed:', error);
      return this._generateFallbackResponse(message, error);
    }
  }

  /**
   * Try to handle timeline query/search requests
   */
  _tryTimelineQuery(message, timelineContext) {
    const queryPatterns = [
      // Search patterns
      { pattern: /(?:find|search|show|get|where are?)\s+(.+)/i, type: 'search' },
      { pattern: /(?:how many|count)\s+(.+)/i, type: 'count' },
      { pattern: /(?:what(?:'s| is) the)\s+(.+)/i, type: 'analyze' },
      { pattern: /(?:list all?|show all?)\s+(.+)/i, type: 'list' },
      { pattern: /(?:best|top|highest|most)\s+(.+)/i, type: 'best' },
      { pattern: /(?:analyze|analysis|structure of)\s+(.+)/i, type: 'analysis' }
    ];

    for (const { pattern, type } of queryPatterns) {
      const match = message.match(pattern);
      if (match) {
        return this._executeTimelineQuery(type, match[1], timelineContext);
      }
    }

    return null;
  }

  /**
   * Execute different types of timeline queries
   */
  _executeTimelineQuery(type, queryText, timelineContext) {
    const timeline = { clips: timelineContext.clips || [], duration: timelineContext.duration || 0 };

    try {
      switch (type) {
        case 'search':
          return this._handleSearchQuery(queryText, timeline);
        case 'count':
          return this._handleCountQuery(queryText, timeline);
        case 'analyze':
          return this._handleAnalyzeQuery(queryText, timeline);
        case 'list':
          return this._handleListQuery(queryText, timeline);
        case 'best':
          return this._handleBestQuery(queryText, timeline);
        case 'analysis':
          return this._handleAnalysisQuery(timeline);
        default:
          return null;
      }
    } catch (error) {
      console.error('Timeline query failed:', error);
      return null;
    }
  }

  _handleSearchQuery(queryText, timeline) {
    const results = timelineQuerySystem.findClipsByDescription(timeline, queryText);
    
    return {
      type: 'search',
      query: queryText,
      results: results,
      count: results.length,
      summary: this._generateSearchSummary(queryText, results)
    };
  }

  _handleCountQuery(queryText, timeline) {
    const results = timelineQuerySystem.findClipsByDescription(timeline, queryText);
    
    return {
      type: 'count',
      query: queryText,
      count: results.length,
      results: results.slice(0, 5), // Show first 5 as examples
      summary: `Found ${results.length} clips matching "${queryText}"`
    };
  }

  _handleAnalyzeQuery(queryText, timeline) {
    const analysis = timelineQuerySystem.analyzeTimeline(timeline);
    
    // Extract relevant analysis based on query
    const relevantData = this._extractRelevantAnalysis(queryText, analysis);
    
    return {
      type: 'analyze',
      query: queryText,
      analysis: relevantData,
      summary: this._generateAnalysisSummary(queryText, relevantData)
    };
  }

  _handleListQuery(queryText, timeline) {
    const results = timelineQuerySystem.findClipsByDescription(timeline, queryText);
    
    return {
      type: 'list',
      query: queryText,
      results: results,
      count: results.length,
      summary: `Here are all ${results.length} clips matching "${queryText}"`
    };
  }

  _handleBestQuery(queryText, timeline) {
    const criteria = this._parseQualityCriteria(queryText);
    const results = timelineQuerySystem.findBestClips(timeline, criteria);
    
    return {
      type: 'best',
      query: queryText,
      results: results,
      criteria: criteria,
      summary: `Here are the top ${results.length} clips for "${queryText}"`
    };
  }

  _handleAnalysisQuery(timeline) {
    const analysis = timelineQuerySystem.analyzeTimeline(timeline);
    
    return {
      type: 'analysis',
      analysis: analysis,
      summary: this._generateFullAnalysisSummary(analysis)
    };
  }

  _generateSearchSummary(query, results) {
    if (results.length === 0) {
      return `No clips found matching "${query}". Try searching for content tags like "talking", "action", or "cooking".`;
    }
    
    if (results.length === 1) {
      return `Found 1 clip matching "${query}": "${results[0].name}"`;
    }
    
    const topResult = results[0];
    return `Found ${results.length} clips matching "${query}". Top result: "${topResult.name}" (${this.formatTime(topResult.duration)})`;
  }

  _generateAnalysisSummary(query, analysis) {
    // Generate human-readable summary based on what was analyzed
    const summaries = {
      duration: `Average clip duration is ${this.formatTime(analysis.averageClipDuration)}`,
      pacing: `Timeline pacing is ${analysis.pacingConsistency > 0.7 ? 'consistent' : 'varied'}`,
      content: `Main content types: ${analysis.dominantTags?.join(', ') || 'mixed'}`,
      structure: `Timeline structure: ${analysis.structure || 'linear'}`,
      quality: `Overall quality score: ${Math.round((analysis.overallScore || 0.5) * 100)}%`
    };
    
    // Find best matching summary
    for (const [key, summary] of Object.entries(summaries)) {
      if (query.toLowerCase().includes(key)) {
        return summary;
      }
    }
    
    return summaries.content; // Default
  }

  _generateFullAnalysisSummary(analysis) {
    const insights = [];
    
    insights.push(`📊 Timeline has ${analysis.totalClips} clips with ${this.formatTime(analysis.totalDuration)} total duration`);
    
    if (analysis.contentDistribution?.dominantTags?.length > 0) {
      insights.push(`🎬 Main content: ${analysis.contentDistribution.dominantTags.slice(0, 3).join(', ')}`);
    }
    
    if (analysis.pacingAnalysis) {
      const consistency = analysis.pacingAnalysis.pacingConsistency || 0;
      insights.push(`⏱️ Pacing: ${consistency > 0.7 ? 'Consistent' : consistency > 0.4 ? 'Varied' : 'Inconsistent'}`);
    }
    
    if (analysis.qualityMetrics?.overallScore) {
      const score = Math.round(analysis.qualityMetrics.overallScore * 100);
      insights.push(`⭐ Quality Score: ${score}%`);
    }
    
    if (analysis.recommendations?.length > 0) {
      insights.push(`💡 Suggestions: ${analysis.recommendations.slice(0, 2).join(', ')}`);
    }
    
    return insights.join('\n');
  }

  _extractRelevantAnalysis(query, fullAnalysis) {
    const queryLower = query.toLowerCase();
    
    if (queryLower.includes('duration')) {
      return {
        averageClipDuration: fullAnalysis.averageClipDuration,
        totalDuration: fullAnalysis.totalDuration,
        durationDistribution: fullAnalysis.pacingAnalysis
      };
    }
    
    if (queryLower.includes('content') || queryLower.includes('tag')) {
      return fullAnalysis.contentDistribution;
    }
    
    if (queryLower.includes('pacing') || queryLower.includes('rhythm')) {
      return fullAnalysis.pacingAnalysis;
    }
    
    if (queryLower.includes('structure')) {
      return fullAnalysis.structure;
    }
    
    if (queryLower.includes('quality')) {
      return fullAnalysis.qualityMetrics;
    }
    
    // Return most relevant subset
    return {
      totalClips: fullAnalysis.totalClips,
      totalDuration: fullAnalysis.totalDuration,
      dominantTags: fullAnalysis.contentDistribution?.dominantTags,
      qualityScore: fullAnalysis.qualityMetrics?.overallScore
    };
  }

  _parseQualityCriteria(queryText) {
    const criteria = { count: 5 };
    
    // Parse count
    const countMatch = queryText.match(/(\d+)/);
    if (countMatch) {
      criteria.count = parseInt(countMatch[1]);
    }
    
    // Parse content preferences
    if (queryText.includes('action')) {
      criteria.preferredTags = ['action'];
    } else if (queryText.includes('talking')) {
      criteria.preferredTags = ['talking'];
    } else if (queryText.includes('cooking')) {
      criteria.preferredTags = ['cooking'];
    }
    
    // Parse duration preferences
    if (queryText.includes('short')) {
      criteria.minDuration = 1;
      criteria.maxDuration = 5;
    } else if (queryText.includes('long')) {
      criteria.minDuration = 10;
    }
    
    return criteria;
  }

  /**
   * Format query response for display
   */
  _formatQueryResponse(queryResult, originalMessage) {
    const response = {
      id: Date.now(),
      role: 'assistant',
      content: queryResult.summary,
      queryResult: queryResult,
      metadata: {
        source: 'query',
        type: queryResult.type,
        resultCount: queryResult.count || queryResult.results?.length || 0,
        executionTime: 0.1,
        safetyScore: 1.0,
        requiresConfirmation: false
      },
      timestamp: new Date().toISOString()
    };

    // Add to conversation history
    this._addToHistory(originalMessage, response);

    return response;
  }

  formatTime(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Try to match user request to predefined templates
   */
  _tryTemplateMatching(message, timelineContext, options = {}) {
    const matches = timelineCodeTemplates.findMatchingTemplates(message, timelineContext, options);
    
    if (matches.length > 0 && matches[0].confidence > 0.8) {
      const bestMatch = matches[0];
      
      try {
        // Generate code from template
        const codeResult = timelineCodeTemplates.generateCode(
          bestMatch.template, 
          bestMatch.suggestedParameters
        );

        return {
          source: 'template',
          template: bestMatch.template,
          confidence: bestMatch.confidence,
          code: codeResult.code,
          explanation: this._generateTemplateExplanation(bestMatch, message),
          executionTime: codeResult.estimatedExecutionTime,
          safetyScore: codeResult.safetyScore,
          requiresConfirmation: this._requiresConfirmation(bestMatch.template)
        };
      } catch (error) {
        console.warn('Template generation failed:', error);
        return null;
      }
    }

    return null;
  }

  /**
   * Generate code using AI backend service with smart context-aware prompts
   */
  async _generateWithAI(message, timelineContext, projectId, sessionId, options = {}) {
    // Generate context-aware prompt using smart code generator
    const smartPrompt = smartCodeGenerator.generateContextAwarePrompt(
      message, 
      timelineContext, 
      options
    );

    // Prepare enhanced context for AI with smart prompt
    const enhancedContext = {
      message: smartPrompt.prompt, // Use the enhanced context-aware prompt
      original_message: message,   // Keep original for reference
      timeline_context: {
        clips: timelineContext.clips || [],
        duration: timelineContext.duration || 0,
        aspectRatio: timelineContext.aspectRatio || '16:9',
        selectedClipIds: timelineContext.selectedClipIds || [],
        availableTags: this._extractAvailableTags(timelineContext.clips || []),
        selectionContext: timelineContext.selectionContext || null,
        // Add smart context analysis
        contextAnalysis: smartPrompt.contextAnalysis,
        estimatedComplexity: smartPrompt.estimatedComplexity
      },
      request_code: true,
      code_generation_context: {
        conversation_history: this.conversationHistory.slice(-5),
        previous_commands: this.codeExecutionHistory.slice(-3),
        debug_mode: this.debugMode,
        mode: options.mode || 'general',
        selectedClips: options.selectedClips || [],
        // Add smart generation context
        promptStrategy: smartPrompt.strategy,
        suggestedModel: smartPrompt.suggestedModel,
        contextAware: true
      }
    };

    // Call enhanced chat API
    const response = await chatAPI.sendMessage(projectId, sessionId, enhancedContext);

    // Optimize the generated code using smart code generator
    let finalCode = response.timeline_code || response.code;
    let optimizationInfo = {};
    
    if (finalCode) {
      const optimizedResult = smartCodeGenerator.optimizeGeneratedCode(
        finalCode, 
        message, 
        timelineContext
      );
      finalCode = optimizedResult.code;
      optimizationInfo = {
        optimizations: optimizedResult.optimizations,
        safetyScore: optimizedResult.safetyScore,
        complexity: optimizedResult.complexity
      };
    }

    const result = {
      source: 'ai_smart',
      message: response.message || response.content,
      code: finalCode,
      confidence: Math.min(smartPrompt.confidence, response.confidence || 0.8),
      explanation: response.explanation || 'AI-generated timeline code with smart optimization',
      executionTime: response.estimated_execution_time || 0.5,
      safetyScore: optimizationInfo.safetyScore || response.safety_score || 0.8,
      requiresConfirmation: response.requires_confirmation || smartPrompt.estimatedComplexity === 'complex',
      intentAnalysis: response.intent_analysis || null,
      // Add smart generation metadata
      smartGeneration: {
        strategy: smartPrompt.strategy,
        promptConfidence: smartPrompt.confidence,
        contextAnalysis: smartPrompt.contextAnalysis,
        optimizations: optimizationInfo.optimizations || [],
        codeComplexity: optimizationInfo.complexity || 1
      },
      rawResponse: response
    };

    // Record the generation for learning
    this._recordSmartGeneration(message, result, timelineContext);

    return result;
  }

  /**
   * Format template response for user
   */
  _formatTemplateResponse(templateMatch, originalMessage) {
    const response = {
      id: Date.now(),
      role: 'assistant',
      content: templateMatch.explanation,
      code: templateMatch.code,
      metadata: {
        source: 'template',
        template: templateMatch.template,
        confidence: templateMatch.confidence,
        executionTime: templateMatch.executionTime,
        safetyScore: templateMatch.safetyScore,
        requiresConfirmation: templateMatch.requiresConfirmation
      },
      timestamp: new Date().toISOString()
    };

    // Add to conversation history
    this._addToHistory(originalMessage, response);

    return response;
  }

  /**
   * Enhance AI response with additional metadata
   */
  _enhanceAIResponse(aiResponse, originalMessage, timelineContext) {
    // Validate and enhance the AI-generated code
    const enhancedCode = this._validateAndEnhanceCode(aiResponse.code, timelineContext);
    
    const response = {
      id: Date.now(),
      role: 'assistant',
      content: aiResponse.message,
      code: enhancedCode.code,
      metadata: {
        source: 'ai',
        confidence: aiResponse.confidence,
        executionTime: enhancedCode.executionTime,
        safetyScore: enhancedCode.safetyScore,
        requiresConfirmation: enhancedCode.requiresConfirmation,
        validationWarnings: enhancedCode.warnings || []
      },
      timestamp: new Date().toISOString()
    };

    // Add to conversation history
    this._addToHistory(originalMessage, response);

    return response;
  }

  /**
   * Generate fallback response when AI fails
   */
  _generateFallbackResponse(message, error) {
    const fallbackSuggestions = [
      "Try: 'Start with ingredients, end with reaction'",
      "Try: 'Cut to 30 seconds'", 
      "Try: 'Remove all talking clips'",
      "Try: 'Create a cooking story'",
      "Try: 'Show me the best moments'"
    ];

    const randomSuggestion = fallbackSuggestions[Math.floor(Math.random() * fallbackSuggestions.length)];

    return {
      id: Date.now(),
      role: 'assistant',
      content: `I had trouble understanding that request. ${randomSuggestion}`,
      code: `// Error: ${error.message}\nconsole.log('Timeline operation failed. Please try a different command.');`,
      metadata: {
        source: 'fallback',
        confidence: 0.1,
        executionTime: 0.1,
        safetyScore: 1.0,
        requiresConfirmation: false,
        error: error.message
      },
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Validate and enhance generated code
   */
  _validateAndEnhanceCode(code, timelineContext) {
    if (!code || typeof code !== 'string') {
      return {
        code: 'console.log("No code was generated");',
        executionTime: 0.1,
        safetyScore: 1.0,
        requiresConfirmation: false,
        warnings: ['No code generated']
      };
    }

    const warnings = [];
    let safetyScore = 1.0;
    let requiresConfirmation = false;

    // Check for dangerous patterns
    const dangerousPatterns = [
      { pattern: /\beval\b/gi, warning: 'Contains eval() call', penalty: 0.5 },
      { pattern: /\bFunction\b/gi, warning: 'Contains Function constructor', penalty: 0.5 },
      { pattern: /\bimport\b/gi, warning: 'Contains import statement', penalty: 0.3 },
      { pattern: /\bfetch\b/gi, warning: 'Contains fetch call', penalty: 0.4 },
      { pattern: /\bdocument\./gi, warning: 'Accesses DOM', penalty: 0.2 },
      { pattern: /\bwindow\./gi, warning: 'Accesses window object', penalty: 0.2 }
    ];

    dangerousPatterns.forEach(({ pattern, warning, penalty }) => {
      if (pattern.test(code)) {
        warnings.push(warning);
        safetyScore *= (1 - penalty);
        requiresConfirmation = true;
      }
    });

    // Check for destructive operations
    const destructivePatterns = [
      /timeline\.clear\(\)/gi,
      /timeline\.removeClip/gi,
      /timeline\.trimClip/gi
    ];

    const hasDestructiveOps = destructivePatterns.some(pattern => pattern.test(code));
    if (hasDestructiveOps) {
      requiresConfirmation = true;
      warnings.push('Contains destructive operations');
    }

    // Estimate execution time based on code complexity
    const lines = code.split('\n').length;
    const loops = (code.match(/for\s*\(/g) || []).length;
    const apiCalls = (code.match(/timeline\./g) || []).length;
    const executionTime = Math.min(0.1 + (lines * 0.01) + (loops * 0.1) + (apiCalls * 0.02), 3.0);

    // Add error handling if missing and safety score is low
    let enhancedCode = code;
    if (safetyScore < 0.8 && !code.includes('try {')) {
      enhancedCode = `try {
${code.split('\n').map(line => '  ' + line).join('\n')}
} catch (error) {
  console.error('Timeline operation failed:', error.message);
}`;
    }

    return {
      code: enhancedCode,
      executionTime,
      safetyScore,
      requiresConfirmation,
      warnings
    };
  }

  /**
   * Generate explanation for template matches
   */
  _generateTemplateExplanation(templateMatch, originalMessage) {
    const templateInfo = timelineCodeTemplates.getTemplate(templateMatch.template);
    
    const explanations = {
      startEndPattern: `I'll arrange your timeline to ${originalMessage.toLowerCase()}. This will place specific content at the beginning and end of your video.`,
      cookingStory: `I'll organize your clips into a logical cooking story: ingredients → preparation → cooking → final result.`,
      cutToLength: `I'll intelligently cut your timeline to the specified duration, keeping the most important content.`,
      removeContent: `I'll remove all clips with the specified content tags from your timeline.`,
      createMontage: `I'll create a fast-paced montage from your action clips.`,
      arrangeByEnergy: `I'll arrange your clips from low to high energy for maximum impact.`,
      findBestMoments: `I'll select and arrange the best moments from your footage.`,
      balanceContent: `I'll evenly distribute different content types throughout your timeline.`
    };

    return explanations[templateMatch.template] || templateInfo.description;
  }

  /**
   * Check if template requires user confirmation
   */
  _requiresConfirmation(templateName) {
    const destructiveTemplates = ['removeContent', 'cutToLength', 'splitLongClips'];
    return destructiveTemplates.includes(templateName);
  }

  /**
   * Extract available content tags from clips
   */
  _extractAvailableTags(clips) {
    const allTags = new Set();
    clips.forEach(clip => {
      if (clip.tags && Array.isArray(clip.tags)) {
        clip.tags.forEach(tag => allTags.add(tag));
      }
    });
    return Array.from(allTags);
  }

  /**
   * Add message to conversation history
   */
  _addToHistory(userMessage, assistantResponse) {
    this.conversationHistory.push({
      user: userMessage,
      assistant: assistantResponse.content,
      code: assistantResponse.code,
      timestamp: new Date().toISOString()
    });

    // Keep only last 10 conversations
    if (this.conversationHistory.length > 10) {
      this.conversationHistory = this.conversationHistory.slice(-10);
    }
  }

  /**
   * Record successful code execution
   */
  recordCodeExecution(code, result, executionTime) {
    const executionRecord = {
      code: code.substring(0, 200), // Store first 200 chars
      success: result.success,
      executionTime,
      timestamp: new Date().toISOString(),
      error: result.error || null
    };

    this.codeExecutionHistory.push(executionRecord);

    // Keep only last 5 executions
    if (this.codeExecutionHistory.length > 5) {
      this.codeExecutionHistory = this.codeExecutionHistory.slice(-5);
    }

    // Also record in smart code generator for learning
    if (result.timelineContext) {
      smartCodeGenerator.learnFromExecution(
        result.originalRequest || 'unknown',
        code,
        result,
        result.timelineContext
      );
    }
  }

  /**
   * Record smart generation for learning and analytics
   */
  _recordSmartGeneration(originalMessage, result, timelineContext) {
    try {
      // This helps the smart code generator learn from successful patterns
      console.debug('Smart generation recorded:', {
        strategy: result.smartGeneration?.strategy,
        confidence: result.confidence,
        optimizations: result.smartGeneration?.optimizations?.length || 0,
        complexity: result.smartGeneration?.codeComplexity
      });
    } catch (error) {
      console.warn('Failed to record smart generation:', error);
    }
  }

  /**
   * Get conversation context for debugging
   */
  getConversationContext() {
    return {
      history: this.conversationHistory,
      executions: this.codeExecutionHistory,
      lastCode: this.lastGeneratedCode
    };
  }

  /**
   * Enable/disable debug mode
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
  }

  /**
   * Clear conversation history
   */
  clearHistory() {
    this.conversationHistory = [];
    this.codeExecutionHistory = [];
    this.lastGeneratedCode = null;
  }

  /**
   * Get smart suggestions based on current timeline state and editing patterns
   */
  getSmartSuggestions(timelineContext) {
    const clips = timelineContext.clips || [];
    const availableTags = this._extractAvailableTags(clips);
    const suggestions = [];

    // Get editing patterns from state manager
    const stateManagerContext = timelineStateManager.getEnhancedContext?.() || {};
    const editingPatterns = stateManagerContext.editingPatterns || {};
    const userPreferences = stateManagerContext.userPreferences || {};

    // Pattern-based suggestions
    if (editingPatterns.frequentOperations?.includes('speed_adjustment')) {
      suggestions.push({
        text: "Speed adjustment",
        command: "Speed up these clips to 1.5x",
        icon: "⚡",
        category: "pattern",
        priority: "high"
      });
    }

    if (editingPatterns.frequentOperations?.includes('arrange_clips')) {
      suggestions.push({
        text: "Arrange clips",
        command: "Arrange clips by energy level",
        icon: "🎬",
        category: "pattern",
        priority: "medium"
      });
    }

    // Content-aware suggestions
    if (availableTags.includes('ingredients') && availableTags.includes('cooking')) {
      suggestions.push({
        text: "Create cooking story",
        command: "Create a cooking story from ingredients to final result",
        icon: "🍳",
        category: "content"
      });
    }

    if (availableTags.includes('action')) {
      suggestions.push({
        text: "Create montage",
        command: "Create a quick montage from action clips",
        icon: "⚡",
        category: "content"
      });
    }

    // Duration-based suggestions
    if (clips.length > 3) {
      const preferredDuration = userPreferences.preferredDuration || 30;
      suggestions.push({
        text: `Cut to ${preferredDuration} seconds`,
        command: `Cut timeline to ${preferredDuration} seconds keeping the best content`,
        icon: "✂️",
        category: "optimization"
      });
    }

    if (timelineContext.duration > 60) {
      suggestions.push({
        text: "Find best moments",
        command: "Show me the 5 best moments from this timeline",
        icon: "⭐",
        category: "discovery"
      });
    }

    // User preference-based suggestions
    if (userPreferences.complexityPreference === 'advanced') {
      suggestions.push({
        text: "Advanced sequence",
        command: "Create an advanced story sequence with transitions",
        icon: "🎭",
        category: "advanced"
      });
    }

    // Always available suggestions (fallback)
    if (suggestions.length < 3) {
      suggestions.push({
        text: "Start with ingredients",
        command: "Start with ingredients, end with reaction",
        icon: "🎬",
        category: "template"
      });
    }

    // Get additional smart suggestions from smart code generator
    const smartSuggestions = smartCodeGenerator.generateSmartSuggestions(timelineContext, {
      maxSuggestions: 3,
      minConfidence: 0.6
    });

    // Merge with existing suggestions
    suggestions.push(...smartSuggestions);

    // Remove duplicates and sort by priority and confidence
    const uniqueSuggestions = suggestions.filter((suggestion, index, self) => 
      index === self.findIndex(s => s.text === suggestion.text)
    );

    return uniqueSuggestions
      .sort((a, b) => {
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        const priorityA = priorityOrder[a.priority] || 1;
        const priorityB = priorityOrder[b.priority] || 1;
        
        if (priorityA !== priorityB) {
          return priorityB - priorityA;
        }
        
        // If same priority, sort by confidence
        return (b.confidence || 0.5) - (a.confidence || 0.5);
      })
      .slice(0, 4); // Return max 4 suggestions
  }
}

// Export singleton instance
const aiChatService = new AIChatService();
export default aiChatService;